import { useCallback, useEffect, useState } from "react";
import { Plus, Save, Trash2, ImageUp } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { staffApi, errMsg } from "@/lib/api";
import { communityImg } from "@/pages/Communities";

const EMPTY = { name: "", tagline: "", description: "", order: 99, active: true };

const Row = ({ c, onSaved }) => {
  const [f, setF] = useState(c);
  const save = async () => {
    const body = { name: f.name, tagline: f.tagline, description: f.description, order: Number(f.order) || 0, active: f.active };
    try { f.id ? await staffApi().put(`/communities/${f.id}`, body) : await staffApi().post("/communities", body); toast.success("Community saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    try { await staffApi().delete(`/communities/${f.id}`); toast.success("Community deleted"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await staffApi().post(`/communities/${f.id}/photo`, fd); toast.success("Photo updated"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tid = f.id || "new";
  return (
    <div className="grid gap-4 rounded-3xl border border-slate-200 bg-white p-5 md:grid-cols-[180px_1fr]" data-testid={`community-row-${tid}`}>
      <div className="space-y-2">
        {f.id ? <img src={communityImg(c)} alt="" className="aspect-[4/3] w-full rounded-2xl object-cover" /> : <div className="aspect-[4/3] rounded-2xl bg-slate-100" />}
        {f.id && <label className="flex cursor-pointer items-center justify-center gap-1 rounded-full border border-slate-200 py-1.5 text-xs text-slate-600 hover:border-[#1D64B4]" data-testid={`community-photo-${tid}`}>
          <ImageUp className="h-3.5 w-3.5" /> Change photo<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} /></label>}
      </div>
      <div className="space-y-2">
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_80px]">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Community name" data-testid={`community-name-${tid}`} />
          <Input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} placeholder="Short tagline" data-testid={`community-tagline-${tid}`} />
          <Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} title="Display order" data-testid={`community-order-${tid}`} />
        </div>
        <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" data-testid={`community-description-${tid}`} />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid={`community-active-${tid}`} /> Shown on website</label>
          <div className="ml-auto flex gap-1">
            <Button size="sm" onClick={save} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid={`community-save-${tid}`}><Save className="mr-1 h-3.5 w-3.5" /> Save</Button>
            {f.id && <Button size="icon" variant="ghost" onClick={remove} className="h-9 w-9 text-rose-500" data-testid={`community-delete-${tid}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminCommunities = () => {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => { staffApi().get("/communities").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const saved = () => { setAdding(false); load(); };
  return (
    <div className="space-y-4" data-testid="admin-communities">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-2xl font-bold tracking-tight text-slate-900">Communities</h2>
          <p className="text-sm text-slate-500">Add, edit or hide communities. Assign events to a community in the calendar, and give leaders access under Administrators.</p></div>
        <Button variant="outline" onClick={() => setAdding(true)} className="rounded-full" data-testid="community-add-button"><Plus className="mr-1 h-4 w-4" /> Add community</Button>
      </div>
      {adding && <Row c={EMPTY} onSaved={saved} />}
      {list.map((c) => <Row key={`${c.id}-${c.has_photo}`} c={c} onSaved={saved} />)}
    </div>
  );
};
